import { useState, useEffect } from 'react';
import { X, Save, Loader2, Users, Plus, Trash2, MapPin, Mail, Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import type { Client } from '../lib/store';

type Addr = { address_line: string; latitude?: number | null; longitude?: number | null };

export default function ClientFormModal({
  client,
  onClose,
  onSaved,
}: {
  client?: Client | null;                       // omit or null -> create
  onClose: () => void;
  onSaved: (c: Client) => void;                 // returns the fresh client (created or updated)
}) {
  const isEditing = !!client?.id;

  const [name, setName] = useState<string>(client?.name || '');
  const [email, setEmail] = useState<string>(client?.email || '');
  const [phone, setPhone] = useState<string>(client?.phone || '');
  const [addresses, setAddresses] = useState<Addr[]>(
    client?.addresses?.length
      ? client.addresses.map((a) => ({
          address_line: a.address_line,
          latitude: a.latitude ?? null,
          longitude: a.longitude ?? null,
        }))
      : [{ address_line: '', latitude: null, longitude: null }]
  );

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    // Focus first field on open
    const el = document.getElementById('client-form-name') as HTMLInputElement | null;
    setTimeout(() => el?.focus(), 50);
  }, []);

  const setAddress = (i: number, value: string) =>
    setAddresses((arr) => arr.map((a, idx) => (idx === i ? { ...a, address_line: value } : a)));
  const addAddress = () =>
    setAddresses((arr) => [...arr, { address_line: '', latitude: null, longitude: null }]);
  const removeAddress = (i: number) =>
    setAddresses((arr) => (arr.length === 1 ? arr : arr.filter((_, idx) => idx !== i)));

  const save = async () => {
    const cleanName = name.trim();
    if (!cleanName) {
      toast.error('Please enter a client name');
      return;
    }
    const cleanAddresses = addresses
      .map((a) => ({
        address_line: (a.address_line || '').trim(),
        latitude: a.latitude ?? null,
        longitude: a.longitude ?? null,
      }))
      .filter((a) => a.address_line.length > 0);

    setSaving(true);
    try {
      const payload: any = {
        name: cleanName,
        email: email.trim() || null,
        phone: phone.trim() || null,
        addresses: cleanAddresses,
      };
      let res;
      if (isEditing) {
        res = await api.put(`/clients/${client!.id}`, payload);
      } else {
        res = await api.post('/clients', payload);
      }
      toast.success(isEditing ? 'Client updated' : 'Client added');
      onSaved(res.data);
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!isEditing) return;
    if (!confirm(`Delete "${client!.name}"? This can't be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/clients/${client!.id}`);
      toast.success('Client deleted');
      onSaved({ ...(client as Client), id: '' }); // signal deletion by empty id
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-pop overflow-hidden max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-50 grid place-items-center">
              <Users className="h-5 w-5 text-brand-700" />
            </div>
            <div>
              <h2 className="font-bold text-slate-900">
                {isEditing ? 'Edit client' : 'Add client'}
              </h2>
              <p className="text-xs text-slate-500">
                {isEditing
                  ? 'Update details or delete this client.'
                  : 'Create a client manually. Not linked to Xero — you can link later.'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <Field label="Client name *">
            <input
              id="client-form-name"
              className="input"
              placeholder="e.g. Mr &amp; Mrs Wilson"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Email">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="email"
                  className="input pl-9"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </Field>
            <Field label="Phone">
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  className="input pl-9"
                  placeholder="e.g. 07700 900123"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </Field>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Addresses</div>
              <button
                type="button"
                onClick={addAddress}
                className="text-xs font-semibold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1"
              >
                <Plus className="h-3 w-3" /> Add another
              </button>
            </div>

            <div className="space-y-2">
              {addresses.map((a, i) => (
                <div key={i} className="flex gap-2">
                  <div className="relative flex-1">
                    <MapPin className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <textarea
                      className="input pl-9 min-h-[52px]"
                      placeholder="Full address (e.g. 12 High Street, Anytown, PA1 2AB)"
                      value={a.address_line}
                      onChange={(e) => setAddress(i, e.target.value)}
                      rows={2}
                    />
                  </div>
                  {addresses.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeAddress(i)}
                      className="self-start mt-1 p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                      title="Remove address"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              Leave blank to skip. Multiple sites (rentals / second homes) are supported.
            </p>
          </div>

          {isEditing && client?.xero_customer_id && (
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg p-3">
              This client is linked to a Xero contact. Invoices auto-attach to that contact when pushed.
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 px-5 py-4 flex items-center justify-between">
          <div>
            {isEditing && (
              <button
                onClick={remove}
                disabled={deleting || saving}
                className="text-sm font-semibold text-red-600 hover:text-red-700 inline-flex items-center gap-1 disabled:opacity-50"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn btn-secondary" disabled={saving || deleting}>
              Cancel
            </button>
            <button onClick={save} disabled={saving || deleting} className="btn btn-primary">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {isEditing ? 'Save changes' : 'Add client'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-[11px] font-semibold text-slate-500 mb-1 uppercase tracking-wider">{label}</div>
      {children}
    </label>
  );
}
